import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  STANDARD_DRAFT_TIME_POLL_API_PATH,
  STANDARD_DRAFT_TIME_POLL_DUE_MESSAGE,
  STANDARD_DRAFT_TIME_POLL_DUE_ON,
  STANDARD_DRAFT_TIME_POLL_CLOSE_AT,
  STANDARD_DRAFT_TIME_POLL_EDIT_CUTOFF_AT,
  STANDARD_DRAFT_TIME_POLL_ID,
  STANDARD_DRAFT_TIME_POLL_OPTIONS as SHARED_STANDARD_DRAFT_TIME_OPTIONS,
  STANDARD_DRAFT_TIME_POLL_TITLE,
  type StandardDraftTimePollDocument,
  type StandardDraftTimePollEditMode,
  type StandardDraftTimePollOption,
} from "../shared/standard-draft-time-poll";

export const STANDARD_DRAFT_TIME_POLL_PATH = STANDARD_DRAFT_TIME_POLL_API_PATH;
export const STANDARD_DRAFT_TIME_OPTIONS = SHARED_STANDARD_DRAFT_TIME_OPTIONS;
export type StandardDraftTimeOption = StandardDraftTimePollOption;
export type StandardDraftPollEditMode = StandardDraftTimePollEditMode;
export type StandardDraftPollView = StandardDraftTimePollDocument;

export interface StandardDraftPollApiError extends Error {
  status?: number;
  code?: string;
}

export type StandardDraftPollRequest = <T>(path: string, init?: RequestInit) => Promise<T>;
type SaveState = "idle" | "saving" | "saved" | "error" | "conflict";

interface ConflictState {
  localRanking: string[];
  savedRanking: string[];
}

function rankingsMatch(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((optionId, index) => optionId === right[index]);
}

function validRanking(ranking: readonly string[]): boolean {
  const allowed = new Set<string>(STANDARD_DRAFT_TIME_OPTIONS.map((option) => option.id));
  return ranking.length <= STANDARD_DRAFT_TIME_OPTIONS.length
    && new Set(ranking).size === ranking.length
    && ranking.every((optionId) => allowed.has(optionId));
}

function safeView(view: StandardDraftPollView): StandardDraftPollView {
  const poll = view?.poll;
  const optionsAreCanonical = Array.isArray(poll?.options)
    && poll.options.length === STANDARD_DRAFT_TIME_OPTIONS.length
    && poll.options.every((option, index) => {
      const expected = STANDARD_DRAFT_TIME_OPTIONS[index];
      return option.id === expected?.id
        && option.label === expected.label
        && option.displayOrder === expected.displayOrder
        && option.startsAt === expected.startsAt;
    });
  if (!poll
    || poll.id !== STANDARD_DRAFT_TIME_POLL_ID
    || poll.title !== STANDARD_DRAFT_TIME_POLL_TITLE
    || poll.dueOn !== STANDARD_DRAFT_TIME_POLL_DUE_ON
    || poll.dueMessage !== STANDARD_DRAFT_TIME_POLL_DUE_MESSAGE
    || poll.editCutoffAt !== STANDARD_DRAFT_TIME_POLL_EDIT_CUTOFF_AT
    || poll.closeAt !== STANDARD_DRAFT_TIME_POLL_CLOSE_AT
    || !optionsAreCanonical
    || typeof view.tieBreakOpen !== "boolean"
    || (view.tieBreakOpen && (view.finalization !== null || view.editMode === "closed" || view.editMode === "full"))
    || !(["full", "create", "appendOnly", "locked", "closed"] as const).includes(view.editMode)) {
    throw new Error("The draft-time poll configuration is invalid.");
  }
  const ranking = view.ballot?.rankedOptionIds ?? [];
  const complete = ranking.length === STANDARD_DRAFT_TIME_OPTIONS.length;
  if (!validRanking(ranking)
    || (view.ballot !== null && (
      ranking.length === 0
      || !Number.isInteger(view.ballot.revision)
      || view.ballot.revision < 1
      || !Number.isInteger(view.ballot.updatedAt)
      || view.ballot.updatedAt < 0
      || view.ballot.status !== (complete ? "complete" : "inProgress")
    ))) throw new Error("The saved draft-time ranking is invalid.");

  const finalized = view.finalization !== null;
  const totalsExpected = finalized || (complete && view.editMode !== "closed");
  if (!totalsExpected) {
    if (view.participation !== null || view.results !== null) {
      throw new Error("Draft-time poll totals were exposed before this ranking was complete.");
    }
    return view;
  }

  const participation = view.participation;
  const results = view.results;
  const participationCounts = participation
    ? [participation.electorateCount, participation.complete, participation.inProgress, participation.notStarted]
    : [];
  const resultOptionIds = results?.options.map((option) => option.optionId) ?? [];
  const resultsAreCanonical = Array.isArray(results?.options)
    && results.options.length === STANDARD_DRAFT_TIME_OPTIONS.length
    && new Set(resultOptionIds).size === STANDARD_DRAFT_TIME_OPTIONS.length
    && results.options.every((option, index) => {
      const expected = STANDARD_DRAFT_TIME_OPTIONS.find((candidate) => candidate.id === option.optionId);
      const previous = index > 0 ? results.options[index - 1] : null;
      return Boolean(expected)
        && option.label === expected?.label
        && option.displayOrder === expected.displayOrder
        && Number.isInteger(option.points)
        && option.points >= 0
        && (!previous
          || previous.points > option.points
          || (previous.points === option.points && previous.displayOrder < option.displayOrder));
    });
  const winner = results?.options[0];
  const finalizationIsValid = !finalized || Boolean(
    view.editMode === "closed"
    && Number.isInteger(view.finalization?.finalizedAt)
    && Number(view.finalization?.finalizedAt) >= STANDARD_DRAFT_TIME_POLL_CLOSE_AT
    && winner
    && view.finalization?.winnerOptionId === winner.optionId
    && view.finalization?.winnerPoints === winner.points
    && view.finalization?.winningStandardDraftAt
      === STANDARD_DRAFT_TIME_OPTIONS.find(
        (option) => option.id === view.finalization?.winnerOptionId,
      )?.startsAt,
  );
  if (!participation
    || !participationCounts.every((count) => Number.isInteger(count) && count >= 0)
    || participation.complete + participation.inProgress + participation.notStarted !== participation.electorateCount
    || participation.finished !== (
      participation.electorateCount > 0
      && participation.complete === participation.electorateCount
    )
    || !results
    || !Number.isInteger(results.eligibleBallotCount)
    || results.eligibleBallotCount < 0
    || !resultsAreCanonical
    || !finalizationIsValid) {
    throw new Error("The draft-time poll totals are invalid.");
  }
  return view;
}

export interface StandardDraftPollController {
  view: StandardDraftPollView | null;
  ranking: string[];
  loading: boolean;
  saveState: SaveState;
  message: string;
  conflict: ConflictState | null;
  load: () => Promise<void>;
  add: (optionId: string) => void;
  move: (optionId: string, direction: -1 | 1) => void;
  remove: (optionId: string) => void;
  startOver: () => void;
  retry: () => void;
  useSavedConflict: () => void;
  useLocalConflict: () => void;
}

export function useStandardDraftTimePoll(
  enabled: boolean,
  request: StandardDraftPollRequest,
): StandardDraftPollController {
  const [view, setView] = useState<StandardDraftPollView | null>(null);
  const [ranking, setRanking] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [message, setMessage] = useState("");
  const [conflict, setConflict] = useState<ConflictState | null>(null);
  const activeRef = useRef(true);
  const enabledRef = useRef(enabled);
  const inFlightRef = useRef(false);
  const refreshInFlightRef = useRef(false);
  const refreshRequestedRef = useRef(false);
  const queuedRankingRef = useRef<string[] | null>(null);
  const failedRankingRef = useRef<string[] | null>(null);
  const revisionRef = useRef(0);
  const serverRankingRef = useRef<string[]>([]);
  const rankingRef = useRef<string[]>([]);
  const viewRef = useRef<StandardDraftPollView | null>(null);
  const conflictRef = useRef<ConflictState | null>(null);
  const drainQueueRef = useRef<() => void>(() => undefined);

  const acceptView = useCallback((nextRaw: StandardDraftPollView, replaceLocal: boolean) => {
    const next = safeView(nextRaw);
    const savedRanking = next.ballot?.rankedOptionIds ?? [];
    viewRef.current = next;
    serverRankingRef.current = savedRanking;
    revisionRef.current = next.ballot?.revision ?? 0;
    setView(next);
    if (replaceLocal) {
      rankingRef.current = savedRanking;
      setRanking(savedRanking);
    }
  }, []);

  const fetchView = useCallback(async (silent: boolean) => {
    if (!enabledRef.current) return;
    if (inFlightRef.current || refreshInFlightRef.current
      || failedRankingRef.current || conflictRef.current) {
      if (!failedRankingRef.current && !conflictRef.current) refreshRequestedRef.current = true;
      return;
    }
    refreshInFlightRef.current = true;
    refreshRequestedRef.current = false;
    const previousBallot = viewRef.current?.ballot ?? null;
    if (!silent) setLoading(true);
    try {
      const next = safeView(await request<StandardDraftPollView>(STANDARD_DRAFT_TIME_POLL_PATH));
      if (!activeRef.current || !enabledRef.current) return;
      const becameDirty = queuedRankingRef.current !== null || failedRankingRef.current !== null || conflictRef.current !== null;
      const previousRanking = [...serverRankingRef.current];
      const nextRanking = next.ballot?.rankedOptionIds ?? [];
      const authoritativeChanged = Boolean(previousBallot) !== Boolean(next.ballot)
        || previousBallot?.revision !== next.ballot?.revision
        || !rankingsMatch(previousRanking, nextRanking);
      if (becameDirty && authoritativeChanged) {
        const localRanking = [...rankingRef.current];
        acceptView(next, false);
        queuedRankingRef.current = null;
        failedRankingRef.current = null;
        const nextConflict = { localRanking, savedRanking: [...nextRanking] };
        conflictRef.current = nextConflict;
        setConflict(nextConflict);
        setSaveState("conflict");
        setMessage("This ranking changed in another tab. Choose which version to keep.");
        return;
      }
      acceptView(next, !becameDirty);
      if (!becameDirty) {
        failedRankingRef.current = null;
        conflictRef.current = null;
        setConflict(null);
        if (!silent) {
          setSaveState("idle");
          setMessage("");
        } else if (!rankingsMatch(previousRanking, next.ballot?.rankedOptionIds ?? [])) {
          setSaveState("idle");
          setMessage("Ranking refreshed from the server.");
        }
      }
    } catch (error) {
      if (!activeRef.current || silent) return;
      setSaveState("error");
      setMessage(error instanceof Error ? error.message : "Unable to load the draft-time poll.");
    } finally {
      refreshInFlightRef.current = false;
      if (activeRef.current) {
        if (!silent) setLoading(false);
        if (queuedRankingRef.current) drainQueueRef.current();
        else if (refreshRequestedRef.current && !failedRankingRef.current && !conflictRef.current) {
          window.setTimeout(() => void fetchView(true), 0);
        }
      }
    }
  }, [acceptView, request]);

  const load = useCallback(() => fetchView(false), [fetchView]);
  const refresh = useCallback(() => fetchView(true), [fetchView]);

  const drainQueue = useCallback(async () => {
    if (inFlightRef.current || refreshInFlightRef.current || !enabledRef.current) return;
    inFlightRef.current = true;
    try {
      while (enabledRef.current && queuedRankingRef.current) {
        const target = queuedRankingRef.current;
        queuedRankingRef.current = null;
        if (rankingsMatch(target, serverRankingRef.current)) {
          setSaveState("saved");
          setMessage(target.length === 0 ? "Ranking cleared." : "Ranking saved.");
          continue;
        }
        setSaveState("saving");
        setMessage("");
        try {
          const next = await request<StandardDraftPollView>(STANDARD_DRAFT_TIME_POLL_PATH, {
            method: target.length === 0 ? "DELETE" : "PUT",
            body: JSON.stringify(target.length === 0
              ? { expectedRevision: revisionRef.current }
              : { rankedOptionIds: target, expectedRevision: viewRef.current?.ballot?.revision ?? null }),
          });
          if (!activeRef.current || !enabledRef.current) return;
          acceptView(next, queuedRankingRef.current === null);
          failedRankingRef.current = null;
          if (queuedRankingRef.current === null) {
            setSaveState("saved");
            setMessage(target.length === 0 ? "Ranking cleared." : "Ranking saved.");
          }
        } catch (caught) {
          if (!activeRef.current) return;
          const error = caught as StandardDraftPollApiError;
          const localRanking = [...rankingRef.current];
          if (error.code === "poll_ballot_revision_conflict") {
            try {
              const latest = safeView(await request<StandardDraftPollView>(STANDARD_DRAFT_TIME_POLL_PATH));
              if (!activeRef.current) return;
              acceptView(latest, false);
              queuedRankingRef.current = null;
              failedRankingRef.current = null;
              const nextConflict = { localRanking, savedRanking: latest.ballot?.rankedOptionIds ?? [] };
              conflictRef.current = nextConflict;
              setConflict(nextConflict);
              setSaveState("conflict");
              setMessage("This ranking changed in another tab. Choose which version to keep.");
            } catch (reloadError) {
              failedRankingRef.current = localRanking;
              setSaveState("error");
              setMessage(reloadError instanceof Error ? reloadError.message : "Unable to reload the saved ranking.");
            }
          } else if (error.code === "poll_ballot_edit_locked" || error.code === "poll_closed") {
            queuedRankingRef.current = null;
            failedRankingRef.current = null;
            conflictRef.current = null;
            setConflict(null);
            try {
              const latest = await request<StandardDraftPollView>(STANDARD_DRAFT_TIME_POLL_PATH);
              if (!activeRef.current) return;
              acceptView(latest, true);
              setSaveState("idle");
              setMessage(error.code === "poll_closed"
                ? "Voting is closed and the final result is fixed."
                : "The August 27 editing deadline has passed. Saved ranks are fixed, but you may keep adding unranked times.");
            } catch (reloadError) {
              setSaveState("error");
              setMessage(reloadError instanceof Error ? reloadError.message : "Unable to reload the saved ranking.");
            }
          } else {
            queuedRankingRef.current = null;
            failedRankingRef.current = localRanking;
            setSaveState("error");
            setMessage(error.message || "Your latest ranking was not saved. Retry when ready.");
          }
          break;
        }
      }
    } finally {
      inFlightRef.current = false;
      if (activeRef.current && queuedRankingRef.current) void drainQueue();
      else if (activeRef.current && refreshRequestedRef.current) void refresh();
    }
  }, [acceptView, request, refresh]);
  drainQueueRef.current = () => void drainQueue();

  const queueRanking = useCallback((next: string[]) => {
    if (!validRanking(next)) return;
    rankingRef.current = next;
    setRanking(next);
    conflictRef.current = null;
    setConflict(null);
    failedRankingRef.current = null;
    queuedRankingRef.current = next;
    setSaveState("saving");
    setMessage("");
    void drainQueue();
  }, [drainQueue]);

  useEffect(() => {
    activeRef.current = true;
    enabledRef.current = enabled;
    if (enabled) void load();
    else {
      queuedRankingRef.current = null;
      failedRankingRef.current = null;
      revisionRef.current = 0;
      serverRankingRef.current = [];
      rankingRef.current = [];
      viewRef.current = null;
      setView(null);
      setRanking([]);
      conflictRef.current = null;
      setConflict(null);
      setSaveState("idle");
      setMessage("");
    }
    return () => { activeRef.current = false; };
  }, [enabled, load]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (inFlightRef.current || queuedRankingRef.current || failedRankingRef.current || conflictRef.current) event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const interval = window.setInterval(refreshIfVisible, 15_000);
    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, [enabled, refresh]);

  const canUndo = view?.editMode === "full";
  const add = useCallback((optionId: string) => {
    const mode = viewRef.current?.editMode;
    if (mode && mode !== "locked" && mode !== "closed"
      && !rankingRef.current.includes(optionId) && rankingRef.current.length < 10) {
      queueRanking([...rankingRef.current, optionId]);
    }
  }, [queueRanking]);
  const move = useCallback((optionId: string, direction: -1 | 1) => {
    if (!canUndo) return;
    const current = [...rankingRef.current];
    const index = current.indexOf(optionId);
    const destination = index + direction;
    if (index < 0 || destination < 0 || destination >= current.length) return;
    [current[index], current[destination]] = [current[destination]!, current[index]!];
    queueRanking(current);
  }, [canUndo, queueRanking]);
  const remove = useCallback((optionId: string) => {
    if (canUndo) queueRanking(rankingRef.current.filter((id) => id !== optionId));
  }, [canUndo, queueRanking]);
  const startOver = useCallback(() => {
    if (canUndo && rankingRef.current.length > 0) queueRanking([]);
  }, [canUndo, queueRanking]);
  const retry = useCallback(() => {
    const target = failedRankingRef.current;
    if (target) queueRanking([...target]);
    else void load();
  }, [load, queueRanking]);
  const useSavedConflict = useCallback(() => {
    const saved = conflict?.savedRanking ?? serverRankingRef.current;
    rankingRef.current = saved;
    setRanking(saved);
    conflictRef.current = null;
    setConflict(null);
    setSaveState("idle");
    setMessage("Using the saved ranking.");
  }, [conflict]);
  const useLocalConflict = useCallback(() => {
    if (!conflict) return;
    const saved = conflict.savedRanking;
    const local = conflict.localRanking;
    const mode = viewRef.current?.editMode;
    const allowed = mode === "full"
      || ((mode === "create" || mode === "appendOnly")
        && local.length >= saved.length
        && saved.every((optionId, index) => optionId === local[index]));
    if (!allowed) {
      setMessage("Saved ranks are fixed after August 27. Use the saved ranking, then add any remaining choices.");
      return;
    }
    conflictRef.current = null;
    setConflict(null);
    queueRanking(local);
  }, [conflict, queueRanking]);

  return { view, ranking, loading, saveState, message, conflict, load, add, move, remove, startOver, retry, useSavedConflict, useLocalConflict };
}

function optionLabel(optionId: string, options: readonly StandardDraftTimeOption[]): string {
  return options.find((option) => option.id === optionId)?.label
    ?? STANDARD_DRAFT_TIME_OPTIONS.find((option) => option.id === optionId)?.label
    ?? optionId;
}

function ordinal(rank: number): string {
  const remainder100 = rank % 100;
  if (remainder100 >= 11 && remainder100 <= 13) return `${rank}th`;
  if (rank % 10 === 1) return `${rank}st`;
  if (rank % 10 === 2) return `${rank}nd`;
  if (rank % 10 === 3) return `${rank}rd`;
  return `${rank}th`;
}

export function StandardDraftTimePollEditor({ controller, compact = false }: {
  controller: StandardDraftPollController;
  compact?: boolean;
}) {
  const headingId = useId();
  const statusId = useId();
  const pollRef = useRef<HTMLElement>(null);
  const conflictActionRef = useRef<HTMLButtonElement>(null);
  const retryActionRef = useRef<HTMLButtonElement>(null);
  const recoveryWasOpenRef = useRef(false);
  const options = controller.view?.poll.options?.length === 10 ? controller.view.poll.options : [...STANDARD_DRAFT_TIME_OPTIONS];
  const ranked = controller.ranking;
  const unranked = options.filter((option) => !ranked.includes(option.id));
  const canUndo = controller.view?.editMode === "full";
  const canAdd = Boolean(controller.view)
    && controller.view?.editMode !== "locked"
    && controller.view?.editMode !== "closed";
  const nextRank = ordinal(ranked.length + 1);
  const savedRanking = controller.view?.ballot?.rankedOptionIds ?? [];
  const persistedComplete = savedRanking.length === 10
    && rankingsMatch(ranked, savedRanking)
    && controller.saveState !== "saving"
    && controller.saveState !== "error"
    && controller.saveState !== "conflict";
  const isFinalized = Boolean(controller.view?.finalization);
  const results = isFinalized || persistedComplete ? controller.view?.results ?? null : null;
  const participation = isFinalized || persistedComplete ? controller.view?.participation ?? null : null;
  const winner = isFinalized ? results?.options[0] ?? null : null;
  const pointsByOptionId = new Map<string, number>((results?.options ?? []).map((result) => [result.optionId, result.points]));
  const showSaveError = controller.saveState === "error" && !controller.loading;
  const recoveryMode = controller.conflict ? "conflict" : showSaveError ? "error" : null;
  const liveStatus = controller.loading
    ? "Loading ranking…"
    : controller.saveState === "saving"
      ? "Saving your ranking…"
      : controller.saveState === "saved" || controller.saveState === "error" || controller.saveState === "conflict"
        ? controller.message
        : "";

  const focusNextChoiceOrRanking = useCallback(() => {
    window.requestAnimationFrame(() => {
      const nextPick = pollRef.current?.querySelector<HTMLElement>("[data-poll-unranked-option]:not(:disabled)");
      if (nextPick) nextPick.focus();
      else document.getElementById(`${headingId}-ranked`)?.focus();
    });
  }, [headingId]);

  useEffect(() => {
    const recoveryWasOpen = recoveryWasOpenRef.current;
    if (recoveryMode === "conflict") {
      recoveryWasOpenRef.current = true;
      conflictActionRef.current?.focus();
      return;
    }
    if (recoveryMode === "error") {
      recoveryWasOpenRef.current = true;
      retryActionRef.current?.focus();
      return;
    }
    if (recoveryWasOpen) {
      recoveryWasOpenRef.current = false;
      focusNextChoiceOrRanking();
    }
  }, [focusNextChoiceOrRanking, recoveryMode]);

  function focusAfterPick() {
    focusNextChoiceOrRanking();
  }

  function add(optionId: string) {
    controller.add(optionId);
    focusAfterPick();
  }

  function startOver() {
    controller.startOver();
    focusNextChoiceOrRanking();
  }

  return (
    <section ref={pollRef} className={`surface-card standard-draft-poll ${compact ? "standard-draft-poll--dialog" : ""}`} aria-labelledby={headingId} aria-busy={controller.loading || controller.saveState === "saving"}>
      <div className="card-header standard-draft-poll__header">
        <h2 className="eyebrow" id={headingId} tabIndex={-1}>STANDARD DRAFT SCHEDULING - RANKED CHOICE POLL</h2>
      </div>
      <div className="card-body stack">
        <div className="standard-draft-poll__deadline-row">
          <p className="standard-draft-poll__deadline"><strong>{controller.view?.poll.dueMessage ?? "Please complete your rankings by Thursday, August 27th."}</strong></p>
          <div className="standard-draft-poll__reset-slot">
            {canUndo && ranked.length > 0 && <button className="button button--quiet button--small" type="button" onClick={startOver}>Start over</button>}
          </div>
        </div>
        {controller.view?.editMode === "appendOnly" && <div className="alert alert--info" role="status">{controller.view.tieBreakOpen ? "First place is tied, so voting remains open. " : "The editing deadline has passed. "}Your saved ranks are fixed, but you may keep adding unranked times until all 10 are ranked.</div>}
        {controller.view?.editMode === "create" && <div className="alert alert--info" role="status">{controller.view.tieBreakOpen ? "First place is tied, so voting remains open. " : "The August 27 deadline has passed, but you may still create a ranking. "}Once saved, those ranks are fixed and you may only add remaining choices.</div>}
        {controller.view?.editMode === "locked" && <div className="alert alert--info" role="status">{controller.view.tieBreakOpen ? "First place is tied and voting remains open for managers who have not voted. " : ""}Your complete ranking is saved and fixed because the August 27 editing deadline has passed.</div>}
        {controller.view?.editMode === "closed" && !isFinalized && <div className="alert alert--info" role="status">Voting is closed. The final results are being calculated.</div>}
        {controller.view?.editMode === "closed" && winner && <div className="alert alert--info" role="status"><strong>Voting is closed.</strong> {winner.label} won with {winner.points} points and is now the Standard Draft time.</div>}
        <div id={statusId} className="sr-only" aria-live="polite" aria-atomic="true">{liveStatus}</div>
        {controller.conflict && <div className="alert alert--warning standard-draft-poll__conflict" role="alert">
          <span>This ranking changed in another tab. Choose which version to keep.</span>
          <div className="button-row">
            <button ref={conflictActionRef} className="button button--secondary button--small" type="button" onClick={controller.useSavedConflict}>Use saved ranking</button>
            <button className="button button--primary button--small" type="button" onClick={controller.useLocalConflict}>Use this ranking</button>
          </div>
        </div>}
        {showSaveError && <div className="alert alert--danger standard-draft-poll__save-error" role="alert">
          <span>{controller.message || "Your latest change is not saved. Retry before leaving this page."}</span>
          <button ref={retryActionRef} className="button button--secondary button--small" type="button" onClick={controller.retry}>Retry save</button>
        </div>}

        <div className="standard-draft-poll__lists">
          {isFinalized && results ? <section aria-labelledby={`${headingId}-ranked`}>
            <div className="standard-draft-poll__section-heading standard-draft-poll__ranking-heading"><h3 id={`${headingId}-ranked`} tabIndex={-1}>Final results</h3><span>{results.eligibleBallotCount} ballots</span></div>
            <ol className="standard-draft-poll__ranked">
              {results.options.map((result, index) => <li key={result.optionId} className="standard-draft-poll__row" data-poll-result-option={result.optionId} tabIndex={-1}>
                <span className="standard-draft-poll__rank" aria-label={`Final place ${index + 1}`}>{index + 1}</span>
                <strong>{index === 0 ? "Winner · " : ""}{result.label}</strong>
                <span className="standard-draft-poll__points" aria-label={`${result.points} league points`}>{result.points} pts</span>
              </li>)}
            </ol>
          </section> : <>
          <section className="standard-draft-poll__choices" aria-labelledby={`${headingId}-unranked`}>
            <div className="standard-draft-poll__section-heading standard-draft-poll__pick-heading">
              <h3 id={`${headingId}-unranked`} aria-live="polite" aria-atomic="true">{unranked.length > 0 ? <>Pick your {nextRank} choice</> : "All 10 choices ranked"}</h3>
              <span className="standard-draft-poll__helper" aria-live="polite" aria-atomic="true">
                {participation
                  ? <><span className="standard-draft-poll__helper-line"><strong>{participation.finished ? "Finished" : "Open"}</strong> · {participation.complete} complete</span><span className="standard-draft-poll__helper-line">{participation.inProgress} in progress · {participation.notStarted} not started</span></>
                  : <><span className="standard-draft-poll__helper-line">Rank the dates that work for you.</span><span className="standard-draft-poll__helper-line">1st = 10 pts; 10th = 1 pt.</span></>}
              </span>
            </div>
            <ul className="standard-draft-poll__unranked">
              {unranked.map((option) => <li key={option.id} className="standard-draft-poll__row">
                <button data-poll-unranked-option={option.id} className="button button--secondary button--small" type="button" aria-label={`Pick ${option.label} as your ${nextRank} choice`} disabled={!canAdd} onClick={() => add(option.id)}>Pick</button>
                <strong>{option.label}</strong>
              </li>)}
            </ul>
          </section>

          <section aria-labelledby={`${headingId}-ranked`}>
            <div className="standard-draft-poll__section-heading standard-draft-poll__ranking-heading"><h3 id={`${headingId}-ranked`} tabIndex={-1}>Your ranking so far</h3><span>{ranked.length}/10</span></div>
            <ol className="standard-draft-poll__ranked">
              {ranked.map((optionId, index) => {
                const points = pointsByOptionId.get(optionId);
                return <li key={optionId} className="standard-draft-poll__row" data-poll-ranked-option={optionId} tabIndex={-1}>
                  <span className="standard-draft-poll__rank" aria-label={`Rank ${index + 1}`}>{index + 1}</span>
                  <strong>{optionLabel(optionId, options)}</strong>
                  <span
                    className={`standard-draft-poll__points${points === undefined ? " standard-draft-poll__points--placeholder" : ""}`}
                    aria-hidden={points === undefined ? "true" : undefined}
                    aria-label={points === undefined ? undefined : `${points} league points`}
                  >{points === undefined ? "\u00a0" : `${points} pts`}</span>
                </li>;
              })}
            </ol>
          </section>
          </>}
        </div>
      </div>
    </section>
  );
}

export function StandardDraftTimePollDialog({ open, controller, onClose }: {
  open: boolean;
  controller: StandardDraftPollController;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return <dialog ref={ref} className="modal standard-draft-poll-modal" aria-labelledby={titleId} onCancel={(event) => { event.preventDefault(); onClose(); }}>
    <div className="modal-card standard-draft-poll-modal__card">
      <div className="modal-header"><h2 id={titleId}>Standard Draft time poll</h2><button className="dialog-close" type="button" aria-label="Close draft-time poll" onClick={onClose}><span aria-hidden="true">×</span></button></div>
      <div className="standard-draft-poll-modal__body"><StandardDraftTimePollEditor controller={controller} compact /></div>
    </div>
  </dialog>;
}
